import { getErrorMessage } from '../../utils/errorHandler';

type SetState = (partial: Record<string, unknown>) => void;

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err as { name?: string } | null)?.name === 'AbortError'
  );
}

/**
 * Wraps an async operation with loading/error state management.
 *
 * Handles the common pattern:
 *   set({ [loadingKey]: true, error: null })
 *   try { ... set({ [loadingKey]: false }) }
 *   catch { set({ error, [loadingKey]: false }) }
 *
 * Use directly for simple fetch methods. For methods with optimistic
 * updates, return values, or re-throws, keep the manual pattern.
 *
 * AbortError is treated as a non-error: the loading flag still clears,
 * but `error` is not set — user-cancelled fetches should be silent.
 */
export async function withLoading<T>(
  set: SetState,
  loadingKey: string,
  fn: () => Promise<T>
): Promise<T> {
  set({ [loadingKey]: true, error: null });
  try {
    const result = await fn();
    set({ [loadingKey]: false });
    return result;
  } catch (error) {
    if (isAbortError(error)) {
      set({ [loadingKey]: false });
    } else {
      set({ error: getErrorMessage(error), [loadingKey]: false });
    }
    throw error;
  }
}
